import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ProgressionChart from '../components/ProgressionChart';
import ProgressionTable from '../components/ProgressionTable';
import CategorySummaryTable from '../components/CategorySummaryTable';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Clock, BookOpen, AlertTriangle,
  Target, BarChart3, Users, Filter, Download, LineChart, XCircle,
  ClipboardCheck, CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState([]);
  const [mcqActivityData, setMcqActivityData] = useState([]);
  const [improvementData, setImprovementData] = useState([]);
  const [weakAreasData, setWeakAreasData] = useState([]);
  const [problemMcqsData, setProblemMcqsData] = useState([]);
  const [progressionData, setProgressionData] = useState(null);
  const [progressionTimeRange, setProgressionTimeRange] = useState('all_time');
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [usageStatsData, setUsageStatsData] = useState(null);
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [wrongAnswersData, setWrongAnswersData] = useState(null);
  const [wrongAnswersLoading, setWrongAnswersLoading] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('sessions');
  const { tenant } = useTenant();

  const tenantName = tenant?.name || 'MedMCQ';

  useEffect(() => {
    document.title = `Analytics Dashboard | ${tenantName}`;
  }, [tenantName]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [days]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserProgression(selectedUserId);
    }
  }, [progressionTimeRange]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [sessions, mcqActivity, improvement, weakAreas, problemMcqs] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/session-durations?days=${days}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/mcq-activity?days=${days}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/improvement-metrics?window_size=50`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/weak-areas?min_attempts=20`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/problem-mcqs?min_attempts=10`, { headers })
      ]);

      setSessionData(sessions.data.data || []);
      setMcqActivityData(mcqActivity.data.data || []);
      setImprovementData(improvement.data.data || []);
      setWeakAreasData(weakAreas.data.data || []);
      setProblemMcqsData(problemMcqs.data.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      alert('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgression = async (userId) => {
    try {
      setProgressionLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user/${userId}/progression?time_range=${progressionTimeRange}`,
        { headers }
      );
      setProgressionData(response.data);
    } catch (error) {
      console.error('Error loading user progression:', error);
      setProgressionData(null);
    } finally {
      setProgressionLoading(false);
    }
  };

  const loadUserUsageStats = async (userId) => {
    try {
      setUsageStatsLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user/${userId}/usage-stats`,
        { headers }
      );
      setUsageStatsData(response.data);
    } catch (error) {
      console.error('Error loading user usage stats:', error);
      setUsageStatsData(null);
    } finally {
      setUsageStatsLoading(false);
    }
  };

  const loadUserWrongAnswers = async (userId) => {
    try {
      setWrongAnswersLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user/${userId}/wrong-answers`,
        { headers }
      );
      setWrongAnswersData(response.data);
    } catch (error) {
      console.error('Error loading wrong answers:', error);
      setWrongAnswersData(null);
    } finally {
      setWrongAnswersLoading(false);
    }
  };

  const loadUserDiagnostic = async (userId) => {
    try {
      setDiagnosticLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user/${userId}/diagnostic`,
        { headers }
      );
      setDiagnosticData(response.data);
    } catch (error) {
      console.error('Error loading diagnostic data:', error);
      setDiagnosticData(null);
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    loadUserProgression(userId);
    loadUserUsageStats(userId);
    loadUserWrongAnswers(userId);
    loadUserDiagnostic(userId);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, icon3dColour }) => (
    <div className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow" style={{ boxShadow: 'var(--shadow-card)' }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <Icon3D icon={Icon} colour={icon3dColour || 'turquoise'} size="sm" />
        )}
      </div>
    </div>
  );

  const ProgressBar = ({ percentage, color = 'bg-cyan-500' }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`${color} h-2 rounded-full transition-all duration-300`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  // Calculate summary stats
  const totalUsers = sessionData.length;
  const avgSessionMins = sessionData.length > 0
    ? Math.round(sessionData.reduce((sum, s) => sum + s.avg_session_duration_minutes, 0) / sessionData.length)
    : 0;
  const totalWeakAreas = weakAreasData.reduce((sum, u) => sum + u.weak_area_count, 0);
  const improvingUsers = improvementData.filter(u => u.is_improving).length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg p-8 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Analytics Dashboard</h1>
              <p className="text-lg opacity-90">Manage users, subscriptions, and monitor platform usage</p>
            </div>
            <div className="flex gap-3">
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="px-4 py-2 rounded-lg text-gray-900 font-medium"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Active Students"
            value={totalUsers}
            icon={Users}
            color="text-cyan-600"
            icon3dColour="turquoise"
          />
          <StatCard
            title="Avg Session Duration"
            value={`${avgSessionMins}m`}
            icon={Clock}
            color="text-cyan-600"
            icon3dColour="blue"
          />
          <StatCard
            title="Students Improving"
            value={improvingUsers}
            subtitle={`of ${improvementData.length} tracked`}
            icon={TrendingUp}
            color="text-green-600"
            icon3dColour="green"
          />
          <StatCard
            title="Weak Areas Detected"
            value={totalWeakAreas}
            icon={AlertTriangle}
            color="text-orange-600"
            icon3dColour="orange"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex flex-wrap gap-1 p-1.5 border-b">
            {[
              { id: 'sessions', label: 'Session Duration', icon: Clock },
              { id: 'activity', label: 'MCQ Activity', icon: BookOpen },
              { id: 'improvement', label: 'Improvement', icon: TrendingUp },
              { id: 'weak', label: 'Weak Areas', icon: Target },
              { id: 'problems', label: 'Problem MCQs', icon: AlertTriangle },
              { id: 'progression', label: 'Progression', icon: LineChart },
              { id: 'usage', label: 'Usage Stats', icon: BarChart3 },
              { id: 'wrong', label: 'Wrong Answers', icon: XCircle },
              { id: 'diagnostic', label: 'Diagnostic Tests', icon: ClipboardCheck }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-4 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-all relative overflow-hidden text-white"
                style={activeTab === tab.id ? {
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  boxShadow: 'var(--shadow-3d-btn)',
                } : {
                  background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                {activeTab === tab.id && (
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                )}
                <tab.icon className="w-4 h-4 relative z-10" />
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>

          {/* Session Duration Tab */}
          {activeTab === 'sessions' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Session Duration Analytics</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Avg Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Min/Max</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Total Sessions</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Total Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sessionData.map((user, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{user.user_name || 'No name'}</div>
                          <div className="text-sm text-gray-500">{user.user_email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-cyan-600">{user.avg_session_duration_minutes} min</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {Math.round(user.min_session_duration_seconds / 60)}m / {Math.round(user.max_session_duration_seconds / 60)}m
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{user.total_sessions}</td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{user.total_time_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sessionData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">No session data available</div>
                )}
              </div>
            </div>
          )}

          {/* MCQ Activity Tab */}
          {activeTab === 'activity' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">MCQ Activity Per Session</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Avg per Session</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Min/Max</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Total MCQs</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Total Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {mcqActivityData.map((user, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{user.user_name || 'No name'}</div>
                          <div className="text-sm text-gray-500">{user.user_email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-green-600">{user.avg_mcqs_per_session}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {user.min_mcqs_per_session} / {user.max_mcqs_per_session}
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{user.total_mcqs}</td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{user.total_sessions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mcqActivityData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">No MCQ activity data available</div>
                )}
              </div>
            </div>
          )}

          {/* Improvement Tab */}
          {activeTab === 'improvement' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Student Improvement Metrics</h2>
              <p className="text-sm text-gray-600 mb-4">
                Comparing last 50 questions vs previous 50 questions
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Total Answered</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Recent Accuracy</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Previous Accuracy</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Change</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {improvementData.map((user, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{user.user_name || 'No name'}</div>
                          <div className="text-sm text-gray-500">{user.user_email}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{user.total_questions_answered}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{user.recent_accuracy}%</span>
                            <ProgressBar percentage={user.recent_accuracy} color="bg-cyan-500" />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{user.previous_accuracy}%</span>
                            <ProgressBar percentage={user.previous_accuracy} color="bg-gray-400" />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-bold text-lg ${
                            user.improvement_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {user.improvement_percentage > 0 ? '+' : ''}{user.improvement_percentage}%
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {user.is_improving ? (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <TrendingUp className="w-4 h-4" />
                              Improving
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 font-medium">
                              <TrendingDown className="w-4 h-4" />
                              Declining
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {improvementData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No improvement data available (students need 50+ questions answered)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weak Areas Tab */}
          {activeTab === 'weak' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Weak Areas Detection</h2>
              <p className="text-sm text-gray-600 mb-4">
                Topics where students show no improvement after 20+ attempts
              </p>
              <div className="space-y-6">
                {weakAreasData.map((user, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900">{user.user_name || 'No name'}</h3>
                        <p className="text-sm text-gray-500">{user.user_email}</p>
                      </div>
                      <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full font-bold text-sm">
                        {user.weak_area_count} weak {user.weak_area_count === 1 ? 'area' : 'areas'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {user.weak_areas.map((area, aIdx) => (
                        <div key={aIdx} className="bg-gray-50 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">{area.category}</span>
                            <span className="text-xs text-gray-500">{area.total_attempts} attempts</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">First half:</span>
                              <span className="ml-2 font-semibold">{area.first_half_accuracy}%</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Second half:</span>
                              <span className="ml-2 font-semibold">{area.second_half_accuracy}%</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <span className={`text-xs font-bold ${
                              area.improvement < 0 ? 'text-red-600' : 'text-cyan-600'
                            }`}>
                              {area.improvement > 0 ? '+' : ''}{area.improvement}% change (needs improvement)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {weakAreasData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No weak areas detected (requires 20+ attempts per category)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Problem MCQs Tab */}
          {activeTab === 'problems' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Problem MCQs</h2>
              <p className="text-sm text-gray-600 mb-4">
                Questions with &lt;40% success rate - may have bad wording or indicate knowledge gaps
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Question</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Difficulty</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Attempts</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {problemMcqsData.map((mcq, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-md">{mcq.question_text}</div>
                          <div className="text-xs text-gray-500 mt-1">ID: {mcq.question_id}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs font-medium">
                            {mcq.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            mcq.difficulty === '1' ? 'bg-green-100 text-green-700' :
                            mcq.difficulty === '2' ? 'bg-yellow-100 text-yellow-700' :
                            mcq.difficulty === '3' ? 'bg-cyan-100 text-cyan-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            Level {mcq.difficulty}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{mcq.total_attempts}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-lg ${
                              mcq.success_rate < 30 ? 'text-red-600' : 'text-cyan-600'
                            }`}>
                              {mcq.success_rate}%
                            </span>
                            <ProgressBar percentage={mcq.success_rate} color="bg-red-500" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {problemMcqsData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No problem MCQs detected (requires 10+ attempts per question)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progression Tab */}
          {activeTab === 'progression' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Student Progression Tracking</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Select a student to view their learning progression across their learning journey
                </p>

                {/* Student Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => {
                      const userId = e.target.value;
                      if (userId) {
                        handleUserSelect(userId);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="">Choose a student...</option>
                    {improvementData.map((user) => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.user_name || user.user_email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time Range Toggle */}
                {selectedUserId && (
                  <div className="mb-6 flex gap-2">
                    <button
                      onClick={() => setProgressionTimeRange('all_time')}
                      className="px-4 py-2 rounded-lg font-medium transition-all text-white"
                      style={progressionTimeRange === 'all_time'
                        ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                        : { background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                      }
                    >
                      All Time
                    </button>
                    <button
                      onClick={() => setProgressionTimeRange('last_30_days')}
                      className="px-4 py-2 rounded-lg font-medium transition-all text-white"
                      style={progressionTimeRange === 'last_30_days'
                        ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                        : { background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                      }
                    >
                      Last 30 Days
                    </button>
                  </div>
                )}
              </div>

              {/* Progression Content */}
              {selectedUserId && progressionData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  {/* Overall Progression Chart */}
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    <ProgressionChart
                      data={progressionData.overall_progression}
                      title="Overall Progression"
                      isLoading={progressionLoading}
                      height={300}
                    />
                  </div>

                  {/* Overall Progression Table */}
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    <ProgressionTable
                      data={progressionData.overall_progression}
                      isLoading={progressionLoading}
                    />
                  </div>

                  {/* Category Summary Table */}
                  {progressionData.category_summary && progressionData.category_summary.length > 0 && (
                    <div style={{
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      padding: '1rem'
                    }}>
                      <CategorySummaryTable
                        data={progressionData.category_summary}
                        isLoading={progressionLoading}
                      />
                    </div>
                  )}

                  {/* Per-Category Progression */}
                  {Object.keys(progressionData.category_progression).length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Progression by Category</h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {Object.entries(progressionData.category_progression).map(([category, data]) => (
                          <div key={category} style={{
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            padding: '1rem'
                          }}>
                            <ProgressionChart
                              data={data}
                              category={category}
                              title="Category Progression"
                              isLoading={progressionLoading}
                              height={250}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!selectedUserId && (
                <div className="text-center py-12 text-gray-500">
                  <p>Select a student above to view their progression data</p>
                </div>
              )}
            </div>
          )}

          {/* Usage Stats Tab */}
          {activeTab === 'usage' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">User Study Usage Statistics</h2>
              <p className="text-sm text-gray-600 mb-6">
                Last 10 study sessions with daily statistics
              </p>

              {/* Student Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => {
                    const userId = e.target.value;
                    if (userId) {
                      handleUserSelect(userId);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">Choose a student...</option>
                  {improvementData.map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user_name || user.user_email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Usage Stats Table */}
              {selectedUserId && usageStatsData && usageStatsData.usage_stats && usageStatsData.usage_stats.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">MCQs Answered</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Correct Answers</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Accuracy</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Time (minutes)</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Categories</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usageStatsData.usage_stats.map((stat, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-gray-900 font-medium">{stat.date}</td>
                          <td className="px-6 py-4 text-gray-600">{stat.questions_answered}</td>
                          <td className="px-6 py-4 text-gray-600">{stat.correct_answers}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{stat.accuracy}%</span>
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    stat.accuracy >= 80
                                      ? 'bg-green-500'
                                      : stat.accuracy >= 60
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                  }`}
                                  style={{ width: `${stat.accuracy}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{stat.time_minutes}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {stat.categories_studied && stat.categories_studied.length > 0
                              ? stat.categories_studied.slice(0, 2).join(', ')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedUserId && usageStatsLoading && (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading usage stats...</p>
                </div>
              )}

              {selectedUserId && usageStatsData && (!usageStatsData.usage_stats || usageStatsData.usage_stats.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <p>No usage data available for this student</p>
                </div>
              )}

              {!selectedUserId && (
                <div className="text-center py-12 text-gray-500">
                  <p>Select a student above to view their usage statistics</p>
                </div>
              )}
            </div>
          )}

          {/* Wrong Answers Tab */}
          {activeTab === 'wrong' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Recent Wrong Answers</h2>
              <p className="text-sm text-gray-500 mb-6">
                Last 25 incorrectly answered questions — including whether the question was re-asked and how it went.
              </p>

              {/* Student Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => { if (e.target.value) handleUserSelect(e.target.value); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Choose a student...</option>
                  {improvementData.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.user_name || u.user_email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Summary */}
              {wrongAnswersData && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  Total wrong answers on record: <span className="font-bold">{wrongAnswersData.total_wrong}</span>
                  {' — '}showing last 25
                </div>
              )}

              {/* Table */}
              {selectedUserId && wrongAnswersData?.wrong_answers?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left">Question</th>
                        <th className="px-4 py-3 text-left">Category</th>
                        <th className="px-4 py-3 text-left">Difficulty</th>
                        <th className="px-4 py-3 text-left">Answered</th>
                        <th className="px-4 py-3 text-left">Time</th>
                        <th className="px-4 py-3 text-left">Re-asked?</th>
                        <th className="px-4 py-3 text-left">Re-attempt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {wrongAnswersData.wrong_answers.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 max-w-xs">
                            <p className="text-gray-800 leading-snug">{item.question_preview}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">{item.category}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">Level {item.difficulty}</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {item.answered_at
                              ? new Date(item.answered_at).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {item.time_taken_seconds}s
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.re_asked ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">
                                ↩ Yes ({item.re_attempt_count}×)
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">Not yet</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.re_attempt_result === 'correct' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                                ✓ Got it
                              </span>
                            )}
                            {item.re_attempt_result === 'wrong' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                                ✗ Still wrong
                              </span>
                            )}
                            {!item.re_attempt_result && (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedUserId && wrongAnswersLoading && (
                <div className="text-center py-8 text-gray-500">Loading wrong answers...</div>
              )}

              {selectedUserId && wrongAnswersData?.wrong_answers?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p className="font-medium text-gray-700">No wrong answers found!</p>
                  <p className="text-sm">This student has answered all questions correctly, or hasn't answered enough yet.</p>
                </div>
              )}

              {!selectedUserId && (
                <div className="text-center py-12 text-gray-500">
                  <p>Select a student above to view their wrong answers</p>
                </div>
              )}
            </div>
          )}

          {/* Diagnostic Tests Tab */}
          {activeTab === 'diagnostic' && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Diagnostic Test Results</h2>
              <p className="text-sm text-gray-500 mb-6">View each student's diagnostic test scores, timing, and progress</p>

              {/* User Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => {
                    const userId = e.target.value;
                    if (userId) handleUserSelect(userId);
                  }}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="">Choose a student...</option>
                  {improvementData.map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user_name || user.user_email}
                    </option>
                  ))}
                </select>
              </div>

              {diagnosticLoading && (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {selectedUserId && !diagnosticLoading && diagnosticData && (
                <div className="space-y-6">
                  {/* Overall status */}
                  <div className="flex items-center gap-3 mb-4">
                    {diagnosticData.all_complete ? (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">
                        <CheckCircle2 className="w-4 h-4" /> All 3 tests complete
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                        <ClipboardCheck className="w-4 h-4" /> {diagnosticData.tests_completed?.length || 0}/3 tests complete
                      </span>
                    )}
                  </div>

                  {/* Test cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {diagnosticData.test_results?.map((test) => (
                      <div key={test.test_number} className={`rounded-xl border-2 p-5 ${
                        test.completed ? 'border-emerald-200 bg-emerald-50' :
                        test.questions_answered > 0 ? 'border-amber-200 bg-amber-50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-bold text-gray-900">Test {test.test_number}</h3>
                          {test.completed ? (
                            <span className="text-xs font-bold px-2 py-1 bg-emerald-200 text-emerald-800 rounded-full">Complete</span>
                          ) : test.questions_answered > 0 ? (
                            <span className="text-xs font-bold px-2 py-1 bg-amber-200 text-amber-800 rounded-full">In Progress</span>
                          ) : (
                            <span className="text-xs font-bold px-2 py-1 bg-gray-200 text-gray-600 rounded-full">Not Started</span>
                          )}
                        </div>

                        {/* Score */}
                        <div className="mb-3">
                          <div className="text-3xl font-bold text-gray-900">{test.accuracy}%</div>
                          <div className="text-sm text-gray-600">{test.correct}/{test.questions_answered} correct</div>
                        </div>

                        {/* Progress bar */}
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progress</span>
                            <span>{test.questions_answered}/{test.questions_total}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${test.completed ? 'bg-emerald-500' : 'bg-amber-500'}`}
                              style={{ width: `${(test.questions_answered / test.questions_total) * 100}%` }}
                            />
                          </div>
                        </div>

                        {/* Time taken */}
                        <div className="text-sm text-gray-600">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {test.time_taken_minutes != null
                            ? `${test.time_taken_minutes} min`
                            : 'No timing data'
                          }
                        </div>

                        {/* Category breakdown */}
                        {test.categories?.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">By Category</p>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                              {test.categories.map((cat, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-700 capitalize truncate mr-2">{cat.category}</span>
                                  <span className={`font-bold whitespace-nowrap ${
                                    cat.accuracy >= 80 ? 'text-emerald-600' :
                                    cat.accuracy >= 60 ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    {cat.accuracy}% ({cat.correct}/{cat.total})
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!selectedUserId && (
                <div className="text-center py-12 text-gray-500">
                  <p>Select a student above to view their diagnostic test results</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default AdminAnalyticsPage;
